'use client';

import { useEffect, useState } from 'react';
import { Badge } from '@govinity/ui/components/ui/badge';
import { MeetingEventDocumentType } from '@govinity/validation';
import { formatDistanceToNow } from 'date-fns';
import { FileText } from 'lucide-react';
import { useTranslations } from 'next-intl';

type MeetingEvent = {
  meetingEventDocuments: Array<{ type: MeetingEventDocumentType }>;
  agenda?: {
    publishedAt: string | Date | null;
    version: number;
    updatedAt: string | Date | null;
  } | null;
};

export const AgendaStatusBadge = ({ meeting }: { meeting: MeetingEvent }) => {
  const t = useTranslations('AgendaStatusBadge');
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Check for AGENDA_OUTLINE document if meetingEventDocuments is available
  const hasAgendaDoc = meeting.meetingEventDocuments.some(
    (doc) =>
      doc.type === MeetingEventDocumentType.AGENDA_OUTLINE ||
      doc.type === MeetingEventDocumentType.AGENDA_PACKAGE
  );

  const agenda = meeting.agenda;

  // Case 1: Agenda is published
  if (agenda?.publishedAt) {
    if (agenda.version === 1) {
      return (
        <Badge variant="success-light">
          <FileText className="h-3 w-3" aria-hidden="true" />
          {t('published')}
        </Badge>
      );
    }

    return (
      <Badge variant="success-light">
        <FileText className="h-3 w-3" aria-hidden="true" />
        {mounted && agenda.updatedAt
          ? t('modified', {
              version: agenda.version,
              time: formatDistanceToNow(agenda.updatedAt),
            })
          : t('modifiedUnknownTime', { version: agenda.version })}
      </Badge>
    );
  }

  // Case 2: No published agenda, but agenda outline or package document exists
  if (hasAgendaDoc) {
    return (
      <Badge variant="success-light">
        <FileText className="h-3 w-3" aria-hidden="true" />
        {t('published')}
      </Badge>
    );
  }

  // Case 3: No agenda and no outline document
  return (
    <Badge variant="warning-light">
      <FileText className="h-3 w-3" aria-hidden="true" />
      {t('pending')}
    </Badge>
  );
};
